import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { uploadFile, useGps, GpsCapture } from "../../lib/field";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, StatCard, StatusBadge, EmptyState, ResponsiveTable } from "../../components/dash/ui";
import { ActivityCard, MediaThumb } from "../../components/activity/ActivityCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ACTIVITY_TYPES, EXPENSE_CATEGORIES, SEVERITY, ROLE_HOME, HARVEST_TYPES, formatDay, formatDate, formatRupiah, isManagerRole } from "../../lib/labels";

export const Spinner = () => <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;
const selectCls = "flex h-12 w-full rounded-md border border-input bg-white px-3 text-base";

export const useGet = (url) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    if (!url) return;
    api.get(url).then((r) => setData(r.data)).catch((e) => { toast.error(formatApiError(e)); setData([]); });
  }, [url]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};

const NewButton = ({ to, label, testid }) => (
  <Button asChild className="h-12 px-6 rounded-full bg-emerald-800 hover:bg-emerald-900 font-bold" data-testid={testid}>
    <Link to={to}><Plus className="h-5 w-5 mr-1.5" /> {label}</Link>
  </Button>
);

export const Timeline = ({ projectId, types, status, testid = "timeline", emptyText = "Belum ada kegiatan kebun kopi yang dilaporkan." }) => {
  const { user } = useAuth();
  const qs = new URLSearchParams({ ...(projectId ? { projectId } : {}), ...(types ? { activityType: types } : {}), ...(status ? { status } : {}) });
  const [rows] = useGet(`/activities?${qs}`);
  if (!rows) return <Spinner />;
  if (!rows.length) return <EmptyState text={emptyText} testid={`${testid}-empty`} />;
  return <div className="grid gap-4 md:grid-cols-2" data-testid={testid}>{rows.map((a) => <ActivityCard key={a.id} a={a} base={ROLE_HOME[user.role]} />)}</div>;
};

export function ActivitiesPage({ types, title = "Kegiatan Kebun Kopi", newType, emptyText }) {
  const { user } = useAuth();
  const base = ROLE_HOME[user.role];
  const [params] = useSearchParams();
  const [projectId, setProjectId] = useState(params.get("projectId") || "");
  const [projects] = useGet(user.role === "SUPER_ADMIN" ? null : "/me/projects");
  const isFarmer = isManagerRole(user.role);
  const subtitle = isFarmer ? "Kegiatan kebun kopi yang Anda kelola. Laporan terkirim dapat dilihat investor terkait dan diverifikasi PT SJI."
    : "Kegiatan di kebun kopi Anda. Status verifikasi PT SJI ditampilkan pada setiap laporan.";
  return (
    <div data-testid="activities-page">
      <PageHeader title={title} subtitle={subtitle}
        action={isFarmer && <NewButton to={`${base}/kegiatan/baru${newType ? `?type=${newType}` : ""}`} label={newType === "PANEN_CHERRY" ? "INPUT PANEN CHERRY" : "INPUT KEGIATAN KEBUN"} testid="new-activity-btn" />} />
      {projects?.length > 1 && (
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className={`${selectCls} max-w-sm mb-5`} data-testid="activity-project-filter">
          <option value="">Semua kebun</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.projectCode} - {p.name}</option>)}
        </select>
      )}
      <Timeline key={projectId} projectId={projectId} types={types} emptyText={emptyText} testid="activities-timeline" />
    </div>
  );
}

export const HarvestPage = () => (
  <div>
    <ActivitiesPage types={HARVEST_TYPES} title="Panen & Produksi Kopi" newType="PANEN_CHERRY" emptyText="Belum ada data produksi kopi." />
    <p className="mt-6 text-xs text-slate-500" data-testid="harvest-flow">Alur: Panen Cherry → Sortasi → Pascapanen → Pengeringan → Hulling → Green Bean → Grading → Stok → Penjualan / Offtake</p>
  </div>
);

export const ExpenseTable = ({ projectId }) => {
  const { user } = useAuth();
  const [rows] = useGet(`/expenses${projectId ? `?projectId=${projectId}` : ""}`);
  if (!rows) return <Spinner />;
  const verified = rows.filter((r) => r.activityStatus === "VERIFIED").reduce((s, r) => s + r.amount, 0);
  const pending = rows.filter((r) => ["SUBMITTED", "UNDER_REVIEW", "REVISION_REQUIRED"].includes(r.activityStatus)).reduce((s, r) => s + r.amount, 0);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <StatCard label="Biaya Terverifikasi" value={formatRupiah(verified)} testid="expense-verified-total" />
        <StatCard label="Menunggu Verifikasi" value={formatRupiah(pending)} testid="expense-pending-total" />
      </div>
      <ResponsiveTable testid="expenses-table" rows={rows} empty="Belum ada biaya tercatat."
        columns={[{ key: "activityDate", label: "Tanggal", render: (r) => formatDay(r.activityDate) },
          { key: "projectCode", label: "Kebun", render: (r) => <span className="font-mono text-xs">{r.projectCode}</span> },
          { key: "activityType", label: "Kegiatan", render: (r) => <Link className="text-emerald-800 font-semibold" to={`${ROLE_HOME[user.role]}/kegiatan/${r.farmUpdateId}`}>{ACTIVITY_TYPES[r.activityType]}</Link> },
          { key: "description", label: "Keterangan", render: (r) => `${r.description} (${EXPENSE_CATEGORIES[r.category]})` },
          { key: "amount", label: "Jumlah", render: (r) => <span className="font-semibold whitespace-nowrap">{formatRupiah(r.amount)}</span> },
          { key: "receiptCount", label: "Nota", render: (r) => `${r.receiptCount} file` },
          { key: "activityStatus", label: "Status", render: (r) => <StatusBadge value={r.activityStatus} /> }]} />
    </div>
  );
};

export function ExpensesPage() {
  const { user } = useAuth();
  return (
    <div data-testid="expenses-page">
      <PageHeader title="Biaya & Bukti" subtitle="Setiap biaya terhubung ke satu kegiatan lapangan beserta nota dan foto buktinya."
        action={<NewButton to={`${ROLE_HOME[user.role]}/kegiatan/baru`} label="INPUT BIAYA KEGIATAN" testid="new-expense-btn" />} />
      <ExpenseTable />
    </div>
  );
}

const IssueForm = ({ projects, onDone }) => {
  const [f, setF] = useState({ projectId: projects[0]?.id || "", title: "", description: "", severity: "MEDIUM" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [gps, capture] = useGps();
  const ref = useRef(null);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/issues", { ...f, gps: gps.gps || null });
      for (const [i, file] of files.entries()) {
        await uploadFile(`/issues/${data.id}/media`, file, {}, (p) => setProgress(`Foto ${i + 1}/${files.length}: ${p}%`));
      }
      toast.success("Masalah dilaporkan");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="issue-form">
      <select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value })} required className={selectCls} data-testid="issue-project-select">
        {projects.map((p) => <option key={p.id} value={p.id}>{p.projectCode} - {p.name}</option>)}
      </select>
      <div className="space-y-1.5"><Label>Judul masalah</Label><Input required minLength={3} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-12 text-base" data-testid="issue-title-input" /></div>
      <div className="space-y-1.5"><Label>Uraian</Label><Textarea required minLength={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="text-base" data-testid="issue-description-input" /></div>
      <select value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} className={selectCls} data-testid="issue-severity-select">
        {Object.entries(SEVERITY).map(([k, v]) => <option key={k} value={k}>Tingkat: {v}</option>)}
      </select>
      <GpsCapture state={gps} capture={capture} />
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} data-testid="issue-photo-input" />
      <Button type="button" variant="outline" className="h-12 w-full" onClick={() => ref.current?.click()} data-testid="issue-photo-btn"><Camera className="h-4 w-4 mr-1.5" /> Foto Masalah ({files.length})</Button>
      {progress && <p className="text-xs text-slate-500">{progress}</p>}
      <Button type="submit" disabled={busy} className="h-12 w-full bg-emerald-800 hover:bg-emerald-900" data-testid="issue-submit-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Laporkan Masalah</Button>
    </form>
  );
};

export const IssueList = ({ projectId }) => {
  const { user } = useAuth();
  const [rows, load] = useGet(`/issues${projectId ? `?projectId=${projectId}` : ""}`);
  const setStatus = async (i, status) => {
    try { await api.patch(`/issues/${i.id}`, { status }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!rows) return <Spinner />;
  if (!rows.length) return <EmptyState text="Tidak ada masalah tercatat." testid="issues-empty" />;
  return (
    <ul className="grid gap-4 md:grid-cols-2" data-testid="issues-list">
      {rows.map((i) => (
        <li key={i.id} className="bg-white border border-slate-200/80 rounded-xl p-4 sm:p-5" data-testid={`issue-${i.id}`}>
          <div className="flex flex-wrap justify-between gap-2"><span className="font-mono text-xs text-emerald-700">{i.projectCode} · {SEVERITY[i.severity]}</span><StatusBadge value={i.status} /></div>
          <p className="mt-2 font-semibold text-slate-900">{i.title}</p>
          <p className="text-sm text-slate-600 whitespace-pre-line">{i.description}</p>
          {i.media.length > 0 && <div className="mt-3 grid grid-cols-3 gap-2">{i.media.map((m) => <div key={m.id} className="aspect-square rounded-md overflow-hidden border"><MediaThumb m={m} /></div>)}</div>}
          <p className="mt-3 text-xs text-slate-500">{i.createdByName} · {formatDate(i.createdAt)}{i.gps && " · GPS tercatat"}</p>
          {user.role !== "INVESTOR" && i.status !== "RESOLVED" && (
            <div className="mt-3 flex gap-2">
              {i.status === "OPEN" && <Button size="sm" variant="outline" className="h-10" onClick={() => setStatus(i, "IN_PROGRESS")} data-testid={`issue-progress-${i.id}`}>Sedang Ditangani</Button>}
              <Button size="sm" variant="outline" className="h-10" onClick={() => setStatus(i, "RESOLVED")} data-testid={`issue-resolve-${i.id}`}>Tandai Selesai</Button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

export function IssuesPage() {
  const [projects] = useGet("/me/projects");
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(0);
  return (
    <div data-testid="issues-page">
      <PageHeader title="Masalah Kebun" subtitle="Laporkan hama, penyakit, kerusakan, atau kendala lapangan. Masalah terlihat oleh PT SJI dan investor kebun."
        action={<Button onClick={() => setOpen(true)} className="h-12 px-6 rounded-full bg-emerald-800 hover:bg-emerald-900 font-bold" data-testid="new-issue-btn"><Plus className="h-5 w-5 mr-1.5" /> Laporkan Masalah</Button>} />
      <IssueList key={key} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="issue-dialog">
          <DialogHeader><DialogTitle>Laporkan Masalah Kebun</DialogTitle></DialogHeader>
          {projects && <IssueForm projects={projects} onDone={() => { setOpen(false); setKey((k) => k + 1); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
